<template>
    <div class="costs-by-tag-container">
        <div
            class="tag-sidebar"
            :class="{ 'sidebar-collapsed': sidebarCollapsed }"
        >
            <b-overlay
                :show="status.costTags === 'loading'"
                class="h-100"
            >
                <b-card
                    no-body
                    class="h-100 mb-0"
                >
                    <!-- 접힌 상태: 아이콘만 표시 -->
                    <div
                        v-if="sidebarCollapsed"
                        class="sidebar-collapsed-content"
                    >
                        <b-button
                            variant="flat-secondary"
                            class="sidebar-toggle-btn"
                            @click="toggleSidebar"
                        >
                            <i class="fas fa-chevron-right"></i>
                        </b-button>
                        <div
                            class="collapsed-tag-icon"
                            v-b-tooltip.hover.right
                            :title="tagKey"
                        >
                            <i class="fas fa-tag"></i>
                        </div>
                    </div>

                    <!-- 펼친 상태: 전체 내용 표시 -->
                    <template v-else>
                        <div class="card-header d-flex justify-content-between align-items-center">
                            <div class="card-group-title">
                                <h4 class="mb-0">Tags</h4>
                            </div>
                            <b-button
                                variant="flat-secondary"
                                size="sm"
                                class="sidebar-toggle-btn p-50"
                                @click="toggleSidebar"
                            >
                                <i class="fas fa-chevron-left"></i>
                            </b-button>
                        </div>
                        <div class="card-body">
                            <div
                                v-b-tooltip.hover.right
                                :title="tagKey"
                                class="selected-tag-display"
                            >
                                <i class="fas fa-tag mr-50 text-secondary"></i>
                                <span class="selected-tag-text">{{ tagKey }}</span>
                            </div>
                            <b-form-radio-group
                                v-model="tagKey"
                                class="tagkey-radio-group"
                                stacked
                            >
                                <b-form-radio
                                    v-for="tag in tagOptions"
                                    :key="tag.value"
                                    :value="tag.value"
                                >
                                    <span
                                        v-b-tooltip.hover.right
                                        :title="tag.label"
                                        class="tag-label-truncate"
                                    >
                                        {{ tag.label }}
                                    </span>
                                </b-form-radio>
                            </b-form-radio-group>
                        </div>
                    </template>
                </b-card>
            </b-overlay>
        </div>
        <div class="tag-main-content">
                <b-row>
                    <b-col md="4">
                        <b-overlay
                            :show="status.costTags === 'loading'"
                        >
                            <CostTagsPieChart
                                :pieChartData="pieChartData"
                                tagKey="tagValue"
                                valueName="cost"
                                :theme="theme"
                                :themeColors="themeColors"
                            />
                        </b-overlay>
                    </b-col>
                    <b-col md="8">
                        <b-overlay
                            :show="status.costTags === 'loading'"
                        >
                            <CostTagsLineChart
                                :month="month"
                                :lineChartData="lineChartData"
                                :pieChartData="pieChartData"
                                :yKeys="fullKeys"
                                :isLoading="isLoading"
                                xKey="date"
                                :theme="theme"
                                :themeColors="themeColors"
                            />
                        </b-overlay>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-overlay
                            :show="status.costTags === 'loading'"
                        >
                            <CostTagsTreeList
                                :data="treeData"
                                :month="month"
                                :projectIdx="project.projectIdx"
                                :tagKey="tagKey"
                                @api:getCostTagsByCategory="getCostTagsByCategory"
                                @api:getCostTagsByResourceId="getCostTagsByResourceId"
                            ></CostTagsTreeList>
                        </b-overlay>
                    </b-col>
                </b-row>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { inject } from '@vue/composition-api'
import CostTagsLineChart from './_components/CostTagsLineChart'
import CostTagsPieChart from './_components/CostTagsPieChart'
import CostTagsTreeList from './_components/CostTagsTreeList'
export default {
    name: 'CostsByTagModule',
    props: ['project', 'month'],
    data () {
        return {
            viewCount: 4,
            tagKey: 'user:Name',
            open: false,
            sidebarCollapsed: false
        }
    },
    components: {
        CostTagsLineChart,
        CostTagsPieChart,
        CostTagsTreeList
    },
    setup (props, context) {
        const themeColors = inject('themeColors')
        const theme = inject('theme')
        return {
            theme,
            themeColors
        }
    },
    computed: {
        ...mapGetters({
            dailyTagValues: '$_costsByTag/dailyTagValues',
            tagValues: '$_costsByTag/tagValues',
            treeData: '$_costsByTag/treeData',
            tags: '$_costsByTag/tags',
            status: '$_costsByTag/status',
            availableTags: '$_costsByTag/availableTags'
        }),
        tagOptions () {
            return this.availableTags.map(tag => {
                return {
                    label: tag,
                    value: tag
                }
            })
        },
        fullKeys () {
            return this.pieChartData.map(item => item.tagValue)
        },
        isLoading () {
            return this.status.costTags === 'loading'
        },
        noData () {
            return (this.dailyTagValues.length === 0 && this.tagValues.length === 0)
        },
        checkIfNeedsEtcData () {
            return this.tagValues.length > this.viewCount + 1
        },
        lineChartData () {
            const keyArrOfChartData = this.checkIfNeedsEtcData
                ? this.tagValues.slice(0, this.viewCount).map(item => item.tagValue).concat(['ETC'])
                : this.tagValues.slice().map(item => item.tagValue)

            return this.dailyTagValues.map(item => {
                // This is always contains key for N/A
                const tagValues = item.tagValues.reduce((accumulator, currentValue) => {
                    if (!accumulator.ETC) accumulator.ETC = null
                    if (keyArrOfChartData.indexOf(currentValue.tagValue) > -1) {
                        accumulator[currentValue.tagValue] = currentValue.cost
                    } else {
                        accumulator.ETC = Number(accumulator.ETC) + currentValue.cost
                    }
                    return accumulator
                }, { ...this.setObjectKeyByArrValues(keyArrOfChartData) })
                return {
                    date: item.date,
                    ...tagValues
                }
            })
        },
        pieChartData () {
            let fullData = []
            if (this.checkIfNeedsEtcData) {
                const etcDataForPieChart = this.tagValues.slice(this.viewCount, this.tagValues.length).reduce((accumulator, currentValue) => {
                    accumulator.cost += currentValue.cost
                    return accumulator
                }, { tagValue: 'ETC', cost: 0 })
                fullData = this.tagValues.slice(0, this.viewCount).concat(etcDataForPieChart)
            } else {
                fullData = this.tagValues.slice()
            }
            return fullData.map(this._setCostFormat).sort(this._sorter)
        }
    },
    methods: {
        ...mapActions({
            getCostTags: '$_costsByTag/getCostTags',
            getCostTagsByCategory: '$_costsByTag/getCostTagsByCategory',
            getCostTagsByResourceId: '$_costsByTag/getCostTagsByResourceId'
        }),
        setObjectKeyByArrValues (arr) {
            return arr.slice().reduce((accumulator, currentValue) => {
                if (this.tagValues.filter(item => item.tagValue === currentValue).length > 0) {
                    accumulator[currentValue] = null
                }
                return accumulator
            }, {})
        },
        showDescription () {
            this.open = !this.open
        },
        toggleSidebar () {
            this.sidebarCollapsed = !this.sidebarCollapsed
        },
        _sorter (a, b) {
            if (a.tagValue === 'ETC' || b.tagValue === 'ETC') return 0
            return b.cost - a.cost
        },
        _setCostFormat (item) {
            return {
                ...item,
                label: this.$_getDollar(item.cost)
            }
        }
    },
    watch: {
        project: {
            handler (value, oldValue) {
                this.getCostTags({ projectIdx: value.projectIdx, month: this.month, tagKey: this.tagKey })
            },
            deep: true
        },
        month (value, oldValue) {
            this.getCostTags({ projectIdx: this.project.projectIdx, month: value, tagKey: this.tagKey })
        },
        tagKey (tagKey) {
            this.getCostTags({ projectIdx: this.project.projectIdx, month: this.month, tagKey })
        }
    }
}
</script>

<style lang="scss" scoped>
.costs-by-tag-container {
    display: flex;
    gap: 1rem;
}
.tag-sidebar {
    width: 280px;
    min-width: 280px;
    transition: all 0.3s ease;

    &.sidebar-collapsed {
        width: 60px;
        min-width: 60px;
    }
}
.tag-main-content {
    flex: 1;
    min-width: 0;
}
.sidebar-collapsed-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1rem 0;
}
.sidebar-toggle-btn {
    border: none;
    background: transparent;

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
}
.collapsed-tag-icon {
    margin-top: 1rem;
    font-size: 1.2rem;
    color: #6e6b7b;
    cursor: pointer;
}
.selected-tag-display {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #ebe9f1;
}
.selected-tag-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    color: inherit;
}
.tagkey-radio-group {
    ::v-deep > .custom-control {
        margin-bottom: 0.75rem;

        .custom-control-label {
            width: 100%;
            overflow: hidden;
        }
    }
}
.tag-label-truncate {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}
</style>
